import { useWindowEventListener } from "@redotech/react-util/event";
import { useHandler } from "@redotech/react-util/hook";
import { useEffect, useRef } from "react";

export const SCAN_CHAR = "&";

interface UseBarcodeScannerProps {
  onScan: (barcode: string) => void;
  enabled: boolean;
  autoSubmitDelay?: number;
  minLength?: number;
  captureFocus?: boolean;
  maxKeyGapMs?: number;
}

/**
 * Creates a hidden off-screen input that captures scanner input without
 * triggering the virtual keyboard on mobile/tablet devices. The input uses
 * `inputMode="none"` and `readOnly` to suppress the soft keyboard while
 * still receiving keydown events from hardware scanners.
 */
const isTouchDevice = (): boolean =>
  typeof window !== "undefined" &&
  ("ontouchstart" in window || navigator.maxTouchPoints > 0);

function createScannerInput(): HTMLInputElement {
  const input = document.createElement("input");
  input.setAttribute("inputmode", "none");
  input.readOnly = true;
  input.setAttribute("aria-hidden", "true");
  input.tabIndex = -1;
  Object.assign(input.style, {
    position: "fixed",
    left: "-9999px",
    top: "-9999px",
    width: "1px",
    height: "1px",
    opacity: "0",
    pointerEvents: "none",
  });
  return input;
}

export function useBarcodeScanner({
  onScan,
  enabled,
  autoSubmitDelay,
  minLength = 1,
  captureFocus = true,
  maxKeyGapMs,
}: UseBarcodeScannerProps): void {
  const bufferRef = useRef<string>("");
  const lastKeyAtRef = useRef<number>(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const submitBuffer = useHandler(() => {
    const barcode = bufferRef.current.trim();
    if (barcode && barcode.length >= minLength) {
      onScan(barcode);
    }
    bufferRef.current = "";
  });

  // On non-touch devices, maintain a hidden input that stays focused so
  // hardware scanners can send keydown events. On touch devices, skip the
  // hidden input entirely — focusing any input (even offscreen) can trigger
  // the virtual keyboard on iOS/Android. Hardware scanners still fire keydown
  // events on the document without needing a focused input.
  useEffect(() => {
    if (!enabled || !captureFocus || isTouchDevice()) {
      if (inputRef.current) {
        inputRef.current.remove();
        inputRef.current = null;
      }
      return;
    }

    const input = createScannerInput();
    document.body.append(input);
    inputRef.current = input;

    // Re-focus the hidden input when the user clicks elsewhere on the page,
    // unless they're interacting with a real form element.
    let refocusing = false;
    const refocus = () => {
      if (refocusing) {
        return;
      }
      if (document.activeElement === input) {
        return;
      }
      if (
        document.activeElement instanceof HTMLInputElement ||
        document.activeElement instanceof HTMLTextAreaElement ||
        (document.activeElement instanceof HTMLElement &&
          document.activeElement.isContentEditable)
      ) {
        return;
      }
      refocusing = true;
      try {
        input.focus({ preventScroll: true });
      } finally {
        refocusing = false;
      }
    };

    input.focus({ preventScroll: true });
    document.addEventListener("focusin", refocus);

    return () => {
      document.removeEventListener("focusin", refocus);
      input.remove();
      inputRef.current = null;
    };
  }, [enabled, captureFocus]);

  const handleKeyDown = useHandler((event: KeyboardEvent) => {
    if (!enabled) {
      return;
    }

    // Ignore keyboard input when typing in real form elements
    if (
      event.target instanceof HTMLInputElement &&
      event.target !== inputRef.current
    ) {
      return;
    }
    if (
      event.target instanceof HTMLTextAreaElement ||
      (event.target instanceof HTMLElement && event.target.isContentEditable)
    ) {
      return;
    }

    // Ignore ctrl/cmd shortcuts
    if (event.ctrlKey || event.metaKey) {
      return;
    }

    // Handle scanner ready-command prefix
    if (event.key === SCAN_CHAR) {
      event.preventDefault();
      bufferRef.current = "";
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    if (event.key === "Enter") {
      if (
        maxKeyGapMs !== undefined &&
        Date.now() - lastKeyAtRef.current > maxKeyGapMs
      ) {
        bufferRef.current = "";
      }
      if (!captureFocus && !bufferRef.current) {
        return;
      }
      event.preventDefault();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      submitBuffer();
      return;
    }

    if (event.key.length === 1) {
      event.preventDefault();
      const now = Date.now();
      if (
        maxKeyGapMs !== undefined &&
        now - lastKeyAtRef.current > maxKeyGapMs
      ) {
        bufferRef.current = "";
      }
      lastKeyAtRef.current = now;
      bufferRef.current += event.key;

      // If autoSubmitDelay is set, auto-submit after delay of no input
      if (autoSubmitDelay !== undefined) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
          timeoutRef.current = null;
          submitBuffer();
        }, autoSubmitDelay);
      }
    }
  });

  useWindowEventListener(window, "keydown", handleKeyDown, { capture: true });
}
